import React, { useEffect, useState } from 'react';
import { DocumentCard, DocumentCardDetails, DocumentCardType, IDocumentCardStyles, Stack, StackItem, Image, Text } from '@fluentui/react';
import BreadcrumbComponent from '../../components/breadcrumb/breadcrumb.component';
import LoaderComponent from '../../components/loader/loader.component';
import ButtonPrimaryComponent from '../../components/button-primary/button-primary.component';
import DynamicFormComponent from '../../components/dynamic-form/dynamic-form.component';

import { useNavigate } from 'react-router-dom';
import useAuthStatus from '../../hooks/auth/AuthHook';
import ApiService from '../../services/api/api.service';

import ElementEnum from '../../enums/ElementEnum';
import ItemPermiso from '../../models/ItemPermiso';
import ItemFormulario from '../../models/ItemFormulario';

import formIcon from '../../assets/img48/formulario_48_neg.png';

const cardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ffffff",
        minWidth: "calc(100% - 20px)",
        padding: 10,
        color: "rgb(50, 49, 48)",
        height: 'auto',
    },
};

const selectedCardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ed6d63",
        minWidth: "calc(100% - 20px)",
        padding: 10,
        borderColor: "#ed6d63",
        color: "#ffffff",
        height: 'auto',
        selectors: {
            ":hover": {
                borderColor: "#ed6d63",
            },
        },
    },
};

interface IDynamicFormsPageProps {};

const DynamicFormsPage = (props : IDynamicFormsPageProps) => {
    const navigate = useNavigate();
    const auth = useAuthStatus();
    const formsPermission : ItemPermiso = auth.getPermission(ElementEnum.FORMS);

    const [loading, setLoading] = useState<boolean>(true);
    const [forms, setForms] = useState<ItemFormulario[]>([]);
    const [selectedForm, setSelectedForm] = useState<ItemFormulario>();
    const [newFormOpen, setNewFormOpen] = useState<boolean>(false);
    const [editFormOpen, setEditFormOpen] = useState<boolean>(false);

    useEffect(() => {
        if (auth.user) {
            if (auth.user.ModoBienvenida) {
                navigate('/main/bienvenida/');
            }
            else if (formsPermission.Ver) {
                setLoading(true);
                ApiService.Post<ItemFormulario[]>('OrdenesTrabajo', 'GetFormularios', auth.user).then((forms: ItemFormulario[]) => {
                    setForms(forms);
                    setLoading(false);
                }, (error: string) => {
                    alert(error);
                    setLoading(false);
                });
            }
        }
    }, [auth.user, formsPermission.Ver, navigate]);

    const onRefresh = () : void => {
        if (auth.user && formsPermission.Ver) {
            setLoading(true);
            ApiService.Post<ItemFormulario[]>('OrdenesTrabajo', 'GetFormularios', auth.user).then((forms: ItemFormulario[]) => {
                setForms(forms);
                setLoading(false);
            }, (error: string) => {
                alert(error);
                setLoading(false);
            });
        }
    };

    const onDelete = () : void => {

    };

    const onView = () : void => {

    };

    return (
        <>
            <BreadcrumbComponent items={["Inicio", "Formularios dinámicos"]} />
            {
                (auth.user && formsPermission.Ver) ? (
                    <>
                        <div className="row align-items-center justify-content-end my-3">
                            <div className='col-auto'>
                                <ButtonPrimaryComponent 
                                    onClick={onRefresh} 
                                    icon="Refresh" />
                            </div>
                            {
                                formsPermission.Nuevo ? (
                                    <div className='col-auto'>
                                        <ButtonPrimaryComponent
                                            text="Añadir"
                                            onClick={() => setNewFormOpen(true)}
                                            icon="AddTo" />
                                    </div>
                                ) : (
                                    null
                                )
                            }
                            {
                                formsPermission.Modificar ? (
                                    <div className='col-auto'>
                                        <ButtonPrimaryComponent
                                            text="Editar"
                                            onClick={() => setEditFormOpen(true)} 
                                            icon="Edit"
                                            disabled={!selectedForm} />
                                    </div>
                                ) : (
                                    null
                                )
                            }
                            {
                                formsPermission.Eliminar ? (
                                    <div className='col-auto'>
                                        <ButtonPrimaryComponent
                                            text="Eliminar"
                                            onClick={onDelete} 
                                            icon="ChromeClose"
                                            disabled={!selectedForm} />
                                    </div>
                                ) : (
                                    null
                                )
                            }
                            <div className='col-auto'>
                                <ButtonPrimaryComponent 
                                    text='Vista previa'
                                    onClick={onView} 
                                    icon="EntryView"
                                    disabled={!selectedForm} />
                            </div>
                        </div>
                        <div className="row align-items-center">
                            <div className="col-12">
                                {
                                    forms.map((form: ItemFormulario) => (
                                        <DocumentCard 
                                            styles={(form.Id === selectedForm?.Id) ? selectedCardStyle : cardStyle}
                                            type={DocumentCardType.compact}
                                            id={form.Id.toString()} 
                                            key={form.Id.toString()} 
                                            onClick={() => setSelectedForm(form)}
                                            onDoubleClick={() => {
                                                setSelectedForm(form);
                                                setEditFormOpen(true);
                                            }}>
                                            <DocumentCardDetails>
                                                <Stack horizontal={true}>
                                                    <StackItem>
                                                        <Image src={formIcon} alt="Formulario" />
                                                    </StackItem>
                                                    <StackItem className='p-1'>
                                                        <Text variant="large">
                                                            {form.Descripcion}
                                                        </Text>
                                                    </StackItem>
                                                </Stack>
                                            </DocumentCardDetails>
                                        </DocumentCard>
                                    ))
                                }
                            </div>
                        </div>
                        {
                            newFormOpen ? (
                                <DynamicFormComponent 
                                    isOpen={true} 
                                    onClose={() => setNewFormOpen(false)} 
                                    onConfirm={onRefresh} />
                            ) : (
                                null
                            )
                        }
                        {
                            (editFormOpen && selectedForm) ? (
                                <DynamicFormComponent 
                                    form={selectedForm}
                                    isOpen={true} 
                                    onClose={() => setEditFormOpen(false)} 
                                    onConfirm={onRefresh} />
                            ) : (
                                null
                            )
                        }
                    </>
                ) : (
                    null
                )
            }
            <LoaderComponent loading={loading} />
        </>
    );
};

export default DynamicFormsPage;
